import { NoxusInputField } from "../Form/InputField";
import { ColourPickerField } from "../UI/ColourPicker";
import { useForm } from "react-hook-form";
import { WorkflowTag, TagResponse, Tag as BackendTag } from "@/generated";
import BaseDialog from "../Modals/BaseDialog";
import {
  defaultPickerColourId,
  resolveColourToken,
} from "@/utils/colourTokens";

type FlowTagType = WorkflowTag | TagResponse | BackendTag;

interface IFlowTagModal {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  tag?: FlowTagType;
  isEdit?: boolean;
  onConfirm: (values: { name: string; color: string }) => void;
}

export function FlowTagModal({
  isOpen,
  onOpenChange,
  tag,
  isEdit,
  onConfirm,
}: IFlowTagModal) {
  const form = useForm<{ name: string; color: string }>({
    mode: "onChange",
    defaultValues: {
      name: tag ? ("name" in tag ? tag.name : tag.value)?.trim() || "" : "",
      color: defaultPickerColourId(tag?.color),
    },
  });

  const handleFormSubmit = (values: { name: string; color: string }) => {
    // The picker works in token ids, but the tag API validates `color` as a
    // #RRGGBB hex — persist the token's tint. resolveColourToken maps it back.
    const token = resolveColourToken(values.color);
    onConfirm({ name: values.name.trim(), color: token.tint });
    onOpenChange(false);
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={isEdit ? "Edit tag" : "Create new tag"}
      size="lg"
      footerActions={[
        {
          label: "Cancel",
          props: {
            variant: "ghost",
            onClick: () => onOpenChange(false),
          },
        },
        {
          label: "Save",
          props: {
            type: "submit",
            form: "flow-tag-modal-form",
            disabled: !form.formState.isValid,
          },
        },
      ]}
    >
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        id="flow-tag-modal-form"
      >
        <div className="flex flex-col gap-6 items-start">
          <NoxusInputField
            name="name"
            label="Tag name"
            errors={form.formState.errors}
            control={form.control}
            rules={{
              required: { value: true, message: "Tag name is required." },
              maxLength: { value: 36, message: "Maximum 36 characters." },
            }}
            placeholder="e.g. Important"
            autoFocus
          />
          <ColourPickerField
            name="color"
            label="Color"
            surface="tag"
            control={form.control}
            initialsSource={form.watch("name")}
          />
        </div>
      </form>
    </BaseDialog>
  );
}
